import type { InningScorecard } from "../../leaguesHome/tournaments/tournamentsDetails/matches/matchesDetails/types/matchDetails.d";
import { fullName, inningOrdinal, isLegalBall, isLiveInning, overHeading, overIndexMode, scoreText, sortBalls, teamByName } from "./helpers";
import type { MatchInfo } from "./helpers";
import { Chip, Empty, InningSeg } from "./parts";
import { useInningCommentary } from "./useLiveMatch";

type Props = { matchId: string; match?: MatchInfo; innings: InningScorecard[]; liveIndex: number; matchLive: boolean; sel: number | null; setSel: (i: number) => void };

const OversTab = ({ matchId, match, innings, liveIndex, matchLive, sel, setSel }: Props) => {
    const idx = Math.min(sel ?? liveIndex, Math.max(0, innings.length - 1));
  const inn = innings[idx];
  const live = matchLive && isLiveInning(inn);
  const c = useInningCommentary(matchId, inn?.inningId, live, !!inn);
  if (!innings.length) return <Empty>Over-by-over data will appear once the first ball is bowled.</Empty>;
  const mode = overIndexMode(c.overs);
  const team = teamByName(match, inn.battingTeamName);
  const newest = c.overs[0]?.overNumber;

  return (
    <>
      <div className="toolbar">
        <InningSeg innings={innings} match={match} cur={idx} onChange={setSel} />
        <div className="r">
          <span style={{ fontSize: 12.5, color: "var(--muted)", fontWeight: 600 }}>{live ? "Over in progress" : "Innings complete"}</span>
        </div>
      </div>
      <div className="box">
        <div className="box-h">
          <span className={`innlbl ${live ? "lv" : ""}`}>{live ? `${inningOrdinal(idx)} · live` : inningOrdinal(idx)}</span>
          <h3>{team.name || inn.battingTeamName} · over by over</h3>
          <span className="sub">{scoreText(inn)}</span>
        </div>
        {c.isLoading ? (
          <Empty>Loading overs…</Empty>
        ) : c.isError ? (
          <Empty>Could not load over-by-over data. Try again later.</Empty>
        ) : !c.overs.length ? (
          <Empty>No overs have been bowled in this innings yet.</Empty>
        ) : (
          <div className="tw">
            <table className="ovt">
              <thead>
                <tr>
                  <th className="l">Over</th>
                  <th className="l">Bowler</th>
                  <th className="l">Deliveries</th>
                  <th>Runs</th>
                  <th>Wkts</th>
                  <th>Score</th>
                  <th>RR</th>
                  <th className="l">At the crease after the over</th>
                </tr>
              </thead>
              <tbody>
                {c.overs.map((o) => {
                  const balls = sortBalls(o.ballsResponseList ?? []);
                  const legal = balls.filter(isLegalBall).length;
                  const current = live && o.overNumber === newest && legal < 6;
                  const heading = overHeading(o.overNumber, mode);
                  const rr = (o.currentRunRate ?? 0) > 0 ? Number(o.currentRunRate).toFixed(2) : heading > 0 ? (o.totalRuns / heading).toFixed(2) : "-";
                  const crease = [o.firstBatsman, o.secondBatsman].filter((b) => b && (b.firstName || b.lastName));
                  return (
                    <tr key={o.overNumber} className={current ? "curow" : ""}>
                      <td className="t">
                        {heading}
                        {current ? <span className="crole s">LIVE</span> : null}
                      </td>
                      <td className="t l" style={{ fontWeight: 600 }}>
                        {fullName(o.bowler?.firstName, o.bowler?.lastName) || "-"}
                      </td>
                      <td className="l">
                        <span className="balls">
                          {balls.map((b, i) => (
                            <Chip key={b.ball_id ?? i} ball={b} />
                          ))}
                          {current ? Array.from({ length: Math.max(0, 6 - legal) }).map((_, i) => <span key={`p${i}`} className="bl pend">·</span>) : null}
                        </span>
                      </td>
                      <td className="big">{o.overRuns ?? 0}</td>
                      <td>{o.overWickets || "–"}</td>
                      <td className="big">
                        {o.totalRuns}/{o.totalWickets}
                      </td>
                      <td>{rr}</td>
                      <td className="l">
                        <span className="bat2">
                          {crease.map((b, i) => (
                            <span key={i} style={{ display: "block" }}>
                              {fullName(b.firstName, b.lastName)} <span>{b.runs}* ({b.balls})</span>
                            </span>
                          ))}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {c.hasNextPage ? (
          <div className="box-f">
            <button type="button" className="link" disabled={c.isFetchingNextPage} onClick={c.fetchNextPage}>
              {c.isFetchingNextPage ? "Loading earlier overs…" : "Load earlier overs"}
            </button>
          </div>
        ) : null}
      </div>
    </>
  );
};

export default OversTab;
